import {
    AutomaticUpdatesIcon,
    CrossIcon,
    Heading,
    Pane,
    Text,
    Textarea,
    TextInput,
    TickCircleIcon,
    toaster,
} from 'evergreen-ui';
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import Lesson from '../../../../../../../workflows_new/src/types/models/Lesson';
import Course from '../../../../../../../workflows_new/src/types/models/Course';
import { Checkbox } from '../../../_ui/Checkbox';
import styles from './styles.module.css';
import { Button } from '../../../_ui/Buttons';
const { request } = require('cademy-shared-web');

type StudentAnswers = {
    [key: string]: string | number | string[];
};

type QuizResult = {
    breakdown: {
        [key: string]: boolean;
    };
    feedback_correct: {
        [key: string]: string;
    };
    feedback_wrong: {
        [key: string]: string;
    };
    percentage: number;
    pass: boolean;
    totalCorrect: number;
    totalPossible: number;
};

type BaseResult = {
    _id: string;
    quiz_ref: string;
    lesson_ref: string;
    student_ref: string;
    marked: boolean;
    created_at: number;
    result?: QuizResult;
    draft: boolean;
};

type NotMarkedResponse = BaseResult & {
    marked: false;
    result: undefined;
    submitted_answers: StudentAnswers;
};

type MarkedResponse = BaseResult & {
    marked: true;
    result: QuizResult;
    submitted_answers: StudentAnswers;
};

type Result = MarkedResponse | NotMarkedResponse;

type Record = {
    _id: string;
    quiz_results: {
        [key: string]: Result;
    };
};

type QuestionLabelProps = {
    label: string;
    markedResult?: boolean;
    description?: string;
    htmlFor?: string;
    props?: HTMLLabelElement;
};

const QuestionLabel: FunctionComponent<QuestionLabelProps> = ({
    label,
    markedResult,
    description,
    htmlFor,
    ...props
}) => {
    return (
        <>
            <label
                className="form-field-label"
                style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '24px' }}
                htmlFor={htmlFor}
                {...props}
            >
                {markedResult === true ? (
                    <TickCircleIcon color="var(--green)" />
                ) : markedResult === false ? (
                    <CrossIcon color="var(--red)" />
                ) : (
                    ''
                )}
                {label}
            </label>
            <span className="form-field-description">{description}</span>
        </>
    );
};

type FeedbackProps = {
    feedbackCorrect?: string;
    feedbackWrong?: string;
};

const Feedback: FunctionComponent<FeedbackProps> = ({ feedbackCorrect, feedbackWrong }) => {
    if (!feedbackCorrect && !feedbackWrong) return null;

    return (
        <div className={feedbackCorrect ? styles.feedback_correct : styles.feedback_wrong}>
            <Text>{feedbackCorrect || feedbackWrong}</Text>
        </div>
    );
};

type BaseQuestionProps = {
    studentAnswers: StudentAnswers;
    setStudentAnswers: React.Dispatch<React.SetStateAction<StudentAnswers>>;
    quizDisabled: boolean;
    markedResult?: boolean;
    feedbackCorrect?: string;
    feedbackWrong?: string;
    onQuestionBlur?: () => void;
};

type MultipleChoiceQuestionProps = BaseQuestionProps & {
    question: Lesson.QuizMultipleChoiceQuestionConfig;
};

type ShortAnswerQuestionProps = BaseQuestionProps & {
    question: Lesson.QuizShortAnswerQuestionConfig;
};

type LongTextQuestionProps = BaseQuestionProps & {
    question: Lesson.QuizLongTextQuestionConfig;
};

type NumberQuestionProps = BaseQuestionProps & {
    question: Lesson.QuizNumberQuestionConfig;
};

const MultipleChoiceQuestion: React.FunctionComponent<MultipleChoiceQuestionProps> = ({
    question,
    studentAnswers,
    setStudentAnswers,
    quizDisabled,
    markedResult,
    feedbackCorrect,
    feedbackWrong,
    onQuestionBlur,
}) => {
    const answer = studentAnswers[question._id];
    const selectedOptions = useMemo(() => (Array.isArray(answer) ? answer : []), [answer]);

    const handleCheckboxChanged = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setStudentAnswers((prevStudentAnswers) => {
                if (e.target.checked) {
                    // box selected
                    return {
                        ...prevStudentAnswers,
                        [question._id]: question.multiple_answers_allowed
                            ? [...selectedOptions, e.target.value]
                            : [e.target.value],
                    };
                }

                // box unselected
                return {
                    ...prevStudentAnswers,
                    [question._id]: selectedOptions.filter((item) => item !== e.target.value),
                };
            });
        },
        [question, selectedOptions, setStudentAnswers]
    );

    return (
        <Pane key={question._id} marginY={15} onBlur={onQuestionBlur}>
            <QuestionLabel
                label={question.question}
                markedResult={markedResult}
                htmlFor={question._id}
                description={
                    question.multiple_answers_allowed
                        ? `Select ${question.multiple_answers_match} that apply`
                        : ''
                }
            />
            {question.options.map((option) => {
                return (
                    <Checkbox
                        name={`${question.question} - ${option}`}
                        value={option}
                        key={option}
                        label={option}
                        checked={selectedOptions.includes(option)}
                        disabled={quizDisabled}
                        onChange={handleCheckboxChanged}
                    />
                );
            })}

            <Feedback feedbackCorrect={feedbackCorrect} feedbackWrong={feedbackWrong} />
        </Pane>
    );
};

const ShortAnswerQuestion: React.FunctionComponent<ShortAnswerQuestionProps> = ({
    question,
    studentAnswers,
    setStudentAnswers,
    markedResult,
    quizDisabled,
    feedbackCorrect,
    feedbackWrong,
    onQuestionBlur,
}) => {
    return (
        <Pane key={question._id} marginY={15} onBlur={onQuestionBlur}>
            <QuestionLabel
                label={question.question}
                markedResult={markedResult}
                htmlFor={question._id}
            />
            <TextInput
                name={question.question}
                id={question._id}
                style={{ width: '100%' }}
                disabled={quizDisabled}
                value={studentAnswers[question._id]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setStudentAnswers({ ...studentAnswers, [question._id]: e.target.value })
                }
            />
            <Feedback feedbackCorrect={feedbackCorrect} feedbackWrong={feedbackWrong} />
        </Pane>
    );
};

const LongTextQuestion: React.FunctionComponent<LongTextQuestionProps> = ({
    question,
    studentAnswers,
    setStudentAnswers,
    markedResult,
    quizDisabled,
    onQuestionBlur,
}) => {
    return (
        <Pane key={question._id} marginY={15} onBlur={onQuestionBlur}>
            <QuestionLabel
                label={question.question}
                markedResult={markedResult}
                htmlFor={question._id}
            />
            <Textarea
                name={question.question}
                id={question._id}
                placeholder={question.placeholder}
                resize="vertical"
                disabled={quizDisabled}
                value={studentAnswers[question._id]}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setStudentAnswers({ ...studentAnswers, [question._id]: e.target.value })
                }
            />
        </Pane>
    );
};

const NumberQuestion: React.FunctionComponent<NumberQuestionProps> = ({
    question,
    studentAnswers,
    setStudentAnswers,
    markedResult,
    quizDisabled,
    feedbackCorrect,
    feedbackWrong,
    onQuestionBlur,
}) => {
    return (
        <Pane key={question._id} marginY={15} onBlur={onQuestionBlur}>
            <QuestionLabel
                label={question.question}
                markedResult={markedResult}
                htmlFor={question._id}
            />
            <TextInput
                name={question.question}
                id={question._id}
                type="number"
                style={{ width: '100%' }}
                disabled={quizDisabled}
                value={studentAnswers[question._id]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setStudentAnswers({ ...studentAnswers, [question._id]: Number(e.target.value) })
                }
            />
            <Feedback feedbackCorrect={feedbackCorrect} feedbackWrong={feedbackWrong} />
        </Pane>
    );
};

type QuizContentProps = {
    content: Lesson.QuizContent;
    record: Record;
    recordType: 'booking' | 'order';
    lesson: Lesson.Model & { quiz_responses: Result[] };
    course: Course.Model;
    refreshData: () => void;
};

const createdAtDesc = (a: Result, b: Result) => {
    if (a.created_at > b.created_at) return -1;
    if (a.created_at < b.created_at) return 1;
    return 0;
};

export const QuizContent = ({
    content,
    record,
    recordType,
    lesson,
    course,
    refreshData,
}: QuizContentProps) => {
    const [submitting, setSubmitting] = useState<boolean>(false);
    const savedResult: Result | undefined = useMemo(
        () =>
            lesson.quiz_responses
                .sort(createdAtDesc)
                .find((response) => response.quiz_ref === content._id),
        [lesson, content]
    );
    const [result, setResult] = useState<Result | null>(savedResult || null);
    const savedAnswers = useMemo(
        () => (savedResult ? savedResult.submitted_answers : false),
        [savedResult]
    );
    const [studentAnswers, setStudentAnswers] = useState<StudentAnswers>(
        savedAnswers ? savedAnswers : {}
    );
    const [lastAutoSave, setLastAutoSave] = useState<number>(0);

    const handleAutoSave = useCallback(async () => {
        try {
            await request.post('/quiz_responses/draft', {
                answers: studentAnswers,
                record_type: recordType,
                record_ref: record._id,
                course_ref: course._id,
                lesson_ref: lesson._id,
                quiz_ref: content._id,
            });
            setLastAutoSave(Date.now());
        } catch (error) {
            console.error(error);
        }
    }, [studentAnswers, recordType, record._id, course._id, lesson._id, content._id]);

    const handleSubmit = useCallback(async () => {
        setSubmitting(true);

        try {
            const { data } = await request.post('/quiz_responses', {
                answers: studentAnswers,
                record_type: recordType,
                record_ref: record._id,
                course_ref: course._id,
                lesson_ref: lesson._id,
                quiz_ref: content._id,
            });
            setResult(data);
            setSubmitting(false);

            if (data.marked === true) {
                if (data.result.pass === true) {
                    toaster.success('Quiz passed!');
                    refreshData();
                } else {
                    toaster.warning('Quiz failed.');
                }
            }
        } catch (error) {
            console.error(error);
            setSubmitting(false);
        }
    }, [studentAnswers, recordType, record._id, course._id, lesson._id, content._id, refreshData]);

    const displayResult = useCallback(() => {
        if (!result || result.draft === true) return null;

        if (result.marked !== true) {
            return (
                <Pane className={styles.results_container}>
                    <AutomaticUpdatesIcon size={32} />
                    <Heading>Answers Submitted</Heading>
                    <Text>Someone will mark them shortly.</Text>
                </Pane>
            );
        }

        const { pass } = result.result;

        return (
            <Pane
                className={styles.results_container}
                style={{ background: pass ? 'var(--green25)' : 'var(--red25)' }}
            >
                {pass ? (
                    <TickCircleIcon color="var(--green)" size={32} />
                ) : (
                    <CrossIcon color="var(--red)" size={32} />
                )}
                <Heading>Quiz {pass ? 'Passed' : 'Failed'}</Heading>
                <Heading size={400}>You scored {Math.round(result.result.percentage)}%</Heading>
                <Text>
                    You answered correctly {result.result.totalCorrect} out of{' '}
                    {result.result.totalPossible} questions.
                </Text>
                {pass ? (
                    <Text color="green">Well Done!</Text>
                ) : (
                    <Text color="red">You needed {content.pass_mark}% to pass.</Text>
                )}
            </Pane>
        );
    }, [result, content.pass_mark]);

    const quizDisabled = useMemo(() => {
        if (result && result.marked === false && result.draft === false) return true;
        if (result && result.marked === true && result.result.pass === true) return true;
        if (result && result.marked === true && content.allow_retakes !== true) return true;

        return false;
    }, [content.allow_retakes, result]);

    return (
        <Pane className={styles.quiz_container}>
            <Pane>
                {(content.questions || []).map((question) => {
                    const quizResult = result && result.result ? result.result : null;

                    switch (question.question_type) {
                        case 'multiple choice':
                            return (
                                <MultipleChoiceQuestion
                                    question={question}
                                    studentAnswers={studentAnswers}
                                    setStudentAnswers={setStudentAnswers}
                                    markedResult={
                                        quizResult ? quizResult.breakdown[question._id] : undefined
                                    }
                                    feedbackCorrect={
                                        quizResult
                                            ? quizResult.feedback_correct[question._id]
                                            : undefined
                                    }
                                    feedbackWrong={
                                        quizResult
                                            ? quizResult.feedback_wrong[question._id]
                                            : undefined
                                    }
                                    quizDisabled={quizDisabled}
                                    onQuestionBlur={handleAutoSave}
                                />
                            );

                        case 'short answer':
                            return (
                                <ShortAnswerQuestion
                                    question={question}
                                    studentAnswers={studentAnswers}
                                    setStudentAnswers={setStudentAnswers}
                                    markedResult={
                                        quizResult ? quizResult.breakdown[question._id] : undefined
                                    }
                                    feedbackCorrect={
                                        quizResult
                                            ? quizResult.feedback_correct[question._id]
                                            : undefined
                                    }
                                    feedbackWrong={
                                        quizResult
                                            ? quizResult.feedback_wrong[question._id]
                                            : undefined
                                    }
                                    quizDisabled={quizDisabled}
                                    onQuestionBlur={handleAutoSave}
                                />
                            );

                        case 'number':
                            return (
                                <NumberQuestion
                                    question={question}
                                    studentAnswers={studentAnswers}
                                    setStudentAnswers={setStudentAnswers}
                                    markedResult={
                                        quizResult ? quizResult.breakdown[question._id] : undefined
                                    }
                                    feedbackCorrect={
                                        quizResult
                                            ? quizResult.feedback_correct[question._id]
                                            : undefined
                                    }
                                    feedbackWrong={
                                        quizResult
                                            ? quizResult.feedback_wrong[question._id]
                                            : undefined
                                    }
                                    quizDisabled={quizDisabled}
                                    onQuestionBlur={handleAutoSave}
                                />
                            );

                        case 'long text':
                            return (
                                <LongTextQuestion
                                    question={question}
                                    studentAnswers={studentAnswers}
                                    setStudentAnswers={setStudentAnswers}
                                    markedResult={
                                        quizResult ? quizResult.breakdown[question._id] : undefined
                                    }
                                    feedbackCorrect={
                                        quizResult
                                            ? quizResult.feedback_correct[question._id]
                                            : undefined
                                    }
                                    feedbackWrong={
                                        quizResult
                                            ? quizResult.feedback_wrong[question._id]
                                            : undefined
                                    }
                                    quizDisabled={quizDisabled}
                                    onQuestionBlur={handleAutoSave}
                                />
                            );

                        case 'file upload':
                            return null;

                        default:
                            return null;
                    }
                })}
            </Pane>

            {displayResult()}

            <div className={styles.submit_btn_container}>
                <Button
                    disabled={quizDisabled}
                    onClick={handleSubmit}
                    isLoading={submitting}
                    appearance="primary"
                >
                    {result && result.marked === true ? 'Re-submit' : 'Submit'} Answers
                </Button>
            </div>
            {result && result.marked === true && content.allow_retakes !== true ? (
                <Pane>
                    <Text>
                        You have already submitted answers and resubmissions are not allowed.
                    </Text>
                </Pane>
            ) : null}
            <Pane className={styles.autoSaveContainer}>
                {lastAutoSave ? (
                    <Text>Answers last saved {new Date(lastAutoSave).toLocaleTimeString()}</Text>
                ) : null}
            </Pane>
        </Pane>
    );
};

export default QuizContent;
